import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getTrackerEvents } from "../../lib/api";
import { getGrantFullDetails } from "../../api/grantWriting";
import toast from "react-hot-toast";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

type Grant = {
  id: string;
  title: string;
  blurb: string;
  date: string;
  tone: "red" | "amber" | "green" | "gray";
  savedGrantId?: number | null;
};

type APIEvent = {
  id: number;
  title: string;
  description: string;
  start: string;
  end: string;
  all_day: boolean;
  type: string;
  related_saved_grant: number | null;
  color: string;
  created_at: string;
  updated_at: string;
};

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toKey = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthLabel = (d: Date) =>
  d.toLocaleString(undefined, { month: "long", year: "numeric" });

const CalendarIcon = () => (
  <span className="material-icons text-[20px]">calendar_month</span>
);
const ListIcon = () => (
  <span className="material-icons text-[20px]">list</span>
);

function startOfCalendar(d: Date) {
  const s = new Date(d.getFullYear(), d.getMonth(), 1);
  const first = new Date(s);
  first.setDate(s.getDate() - s.getDay());
  return first;
}

function buildMonthGrid(anchor: Date) {
  const start = startOfCalendar(anchor);
  const days: Date[] = [];
  for (let i = 0; i < 35; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

function parseDateOnly(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function extractDateKey(isoStr: string): string {
  return isoStr.split("T")[0];
}

function calculateTone(dateStr: string): Grant["tone"] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const deadline = parseDateOnly(dateStr);
  deadline.setHours(0, 0, 0, 0);

  const diffDays = Math.floor(
    (deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return "gray";
  if (diffDays <= 7) return "red";
  if (diffDays <= 30) return "amber";
  return "green";
}

function convertAPIEventToGrant(event: APIEvent): Grant {
  const dateStr = extractDateKey(event.end);
  return {
    id: String(event.id),
    title: event.title,
    blurb: event.description,
    date: dateStr,
    tone: calculateTone(dateStr),
    savedGrantId: event.related_saved_grant ?? null,
  };
}

/* ── UI Color Tokens ───────────────────────────────────── */

const tinyDot: Record<Grant["tone"], string> = {
  red: "bg-[#DC4731]",
  amber: "bg-[#D1AB00]",
  green: "bg-[#3DBA82]",
  gray: "bg-[#777B7F]",
};

const pillBg: Record<Grant["tone"], string> = {
  red: "bg-[#FDDBD7] border border-[#F5A898]",
  amber: "bg-[#FFF3CC] border border-[#F0D060]",
  green: "bg-[#C6FDDE] border border-[#7EEAB5]",
  gray: "bg-[#EBEBEB] border border-[#C8C8C8]",
};

const pillAccent: Record<Grant["tone"], string> = {
  red: "bg-[#DC4731]",
  amber: "bg-[#D1AB00]",
  green: "bg-[#3DBA82]",
  gray: "bg-[#777B7F]",
};

const pillText: Record<Grant["tone"], string> = {
  red: "text-[#7D1A0A]",
  amber: "text-[#6B4A00]",
  green: "text-[#0E4D2B]",
  gray: "text-[#4A4A4A]",
};

/* ── Footer Legend ─────────────────────────────────────── */

function FooterLegend() {
  return (
    <div className="px-2 md:px-0 mt-6 mb-1">
      <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#777B7F] font-medium">
        <LegendItem swatch="red" text="Less than 7 days" />
        <LegendItem swatch="amber" text="8–30 days" />
        <LegendItem swatch="green" text="More than 30 days" />
        <LegendItem swatch="gray" text="Passed" />
      </div>
    </div>
  );
}

function LegendItem({ swatch, text }: { swatch: Grant["tone"]; text: string }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className={`h-[10px] w-[10px] rounded-full ${tinyDot[swatch]}`} />
      <span>{text}</span>
    </div>
  );
}

/* ── View Toggle ───────────────────────────────────────── */

function ViewToggle({
  view,
  onChange,
}: {
  view: "cal" | "list";
  onChange: (v: "cal" | "list") => void;
}) {
  return (
    <div className="inline-flex rounded-lg overflow-hidden border border-[#E5E7EB] shadow-sm">
      <button
        aria-pressed={view === "cal"}
        onClick={() => onChange("cal")}
        className={`h-9 w-10 flex items-center justify-center text-primary transition-colors ${
          view === "cal" ? "bg-black" : "bg-white hover:bg-gray-100"
        }`}
        title="Calendar"
      >
        <CalendarIcon />
      </button>
      <div className="w-[1px] bg-gray-200" />
      <button
        aria-pressed={view === "list"}
        onClick={() => onChange("list")}
        className={`h-9 w-10 grid place-items-center text-primary transition-colors ${
          view === "list" ? "bg-black" : "bg-white hover:bg-gray-100"
        }`}
        title="List"
      >
        <ListIcon />
      </button>
    </div>
  );
}

/* ── Month / Year Selector ─────────────────────────────── */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// function MonthYearSelector({
//   month,
//   onChange,
// }: {
//   month: Date;
//   onChange: (d: Date) => void;
// }) {
//   // Build year range: 5 years back, 5 years forward
//   const years: number[] = [];
// for (let y = 1200; y <= 3000; y++) {
//   years.push(y);
// }

//   const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
//     const newMonth = parseInt(e.target.value, 10);
//     onChange(new Date(month.getFullYear(), newMonth, 1));
//   };

//   const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
//     const newYear = parseInt(e.target.value, 10);
//     onChange(new Date(newYear, month.getMonth(), 1));
//   };

//   return (
//     <div className="flex items-center gap-2">
//       {/* Month select */}
//       <div className="relative">
//         <select
//           value={month.getMonth()}
//           onChange={handleMonthChange}
//           className="h-9 appearance-none cursor-pointer rounded-lg border border-[#D6DADF] bg-white pl-3 pr-8 text-sm font-semibold text-[#111] outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
//         >
//           {MONTHS.map((m, i) => (
//             <option key={m} value={i}>
//               {m}
//             </option>
//           ))}
//         </select>
//         <span className="material-icons pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[16px] text-[#6B7280]">
//           expand_more
//         </span>
//       </div>

//       {/* Year select */}
//       <div className="relative">
//         <select
//           value={month.getFullYear()}
//           onChange={handleYearChange}
//           className="h-9 appearance-none cursor-pointer rounded-lg border border-[#D6DADF] bg-white pl-3 pr-8 text-sm font-semibold text-[#111] outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
//         >
//           {years.map((y) => (
//             <option key={y} value={y}>
//               {y}
//             </option>
//           ))}
//         </select>
//         <span className="material-icons pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[16px] text-[#6B7280]">
//           expand_more
//         </span>
//       </div>
//     </div>
//   );
// }
function MonthYearSelector({
  month,
  onChange,
}: {
  month: Date;
  onChange: (d: Date) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<"year" | "month">("year");
  const wrapperRef= React.useRef<HTMLDivElement|null>(null);

  const currentYear = month.getFullYear();
  const currentMonth = month.getMonth();

  const startDecade = Math.floor(currentYear / 10) * 10;

  const years = Array.from({ length: 10 }, (_, i) => startDecade + i);

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", { month: "short" })
  );

  const handleYearSelect = (year: number) => {
    onChange(new Date(year, currentMonth, 1));
    setView("month");
  };

  const handleMonthSelect = (monthIndex: number) => {
    onChange(new Date(currentYear, monthIndex, 1));
    setOpen(false);
    setView("year");
  };

  const changeDecade = (direction: "prev" | "next") => {
    const newYear =
      direction === "prev" ? currentYear - 10 : currentYear + 10;
    onChange(new Date(newYear, currentMonth, 1));
  };
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setView("year");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className="h-9 px-3 rounded-lg border border-[#D6DADF] bg-white text-sm font-semibold hover:bg-gray-50"
      >
        {month.toLocaleString("default", { month: "long" })} {currentYear}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            {view === "year" ? (
              <>
                <button
                  onClick={() => changeDecade("prev")}
                  className="px-2 py-1 hover:bg-gray-100 rounded"
                >
                  ‹
                </button>
                <span className="font-semibold">
                  {startDecade} - {startDecade + 9}
                </span>
                <button
                  onClick={() => changeDecade("next")}
                  className="px-2 py-1 hover:bg-gray-100 rounded"
                >
                  ›
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setView("year")}
                  className="px-2 py-1 rounded"
                >
                  ←
                </button>
                <span className="font-semibold">{currentYear}</span>
                <div />
              </>
            )}
          </div>

          {/* Year View */}
          {view === "year" && (
            <div className="grid grid-cols-4 gap-2">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  className={`py-2 rounded-lg text-sm hover:bg-gray-100 hover:text-black ${
                    year === currentYear
                      ? "bg-primary text-white font-semibold"
                      : ""
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Month View */}
          {view === "month" && (
            <div className="grid grid-cols-4 gap-2">
              {months.map((m, idx) => (
                <button
                  key={m}
                  onClick={() => handleMonthSelect(idx)}
                  className={`py-2 rounded-lg text-sm hover:bg-gray-100 hover:text-black ${
                    idx === currentMonth
                      ? "bg-primary text-white font-semibold"
                      : ""
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
/* ── Track Page ────────────────────────────────────────── */

export default function Track() {
  const nav = useNavigate();
  const [view, setView] = React.useState<"cal" | "list">("cal");
  const [month, setMonth] = React.useState<Date>(new Date());
  const [grants, setGrants] = React.useState<Grant[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [startingId, setStartingId] = React.useState<string | null>(null);

  const fetchGrants = React.useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await getTrackerEvents();
      const events: APIEvent[] = Array.isArray(data) ? data : [];
      const deadlineEvents = events.filter((e) => e.type === "deadline");
      const convertedGrants = deadlineEvents.map(convertAPIEventToGrant);
      setGrants(convertedGrants);
      toast.success("Grant details loaded successfully");
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        err?.response?.data?.detail ||
        "Unable to load grant tracker";
      setLoadError(message);
      setGrants([]);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const handleStart = async (g: Grant) => {
    if (startingId) return;
    if (!g.savedGrantId) {
      alert("Could not find the linked saved grant for this deadline.");
      return;
    }
    try {
      setStartingId(g.id);
      const fullDetails = await getGrantFullDetails(g.savedGrantId);
      const discoveryGrantId =
        fullDetails?.saved_grant?.grant ?? fullDetails?.saved_grant?.grant_id;
      nav(`/dashboard/write/${g.savedGrantId}`, {
        state: {
          saved_grant_id: g.savedGrantId,
          grant: { id: discoveryGrantId ?? g.id, title: g.title },
          details: fullDetails,
        },
      });
    } catch (err: any) {
      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          err?.response?.data?.detail ||
          "Could not open application"
      );
    } finally {
      setStartingId(null);
    }
  };

  return (
    <div className="mx-auto px-5 xl:px-7 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-[32px] font-bold">Grant Tracker</h1>
          <p className="text-md font-medium text-[#777B7F]">
            Visualize and manage your grant application deadlines.
          </p>
        </div>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {/* Error Banner — only when the fetch actually failed */}
      {!loading && loadError && (
        <div className="bg-red-50 border border-red-200 flex items-center justify-between rounded-lg p-4 mb-6">
          <p className="text-red-800 font-semibold">Error Loading Grants</p>
          <button
            onClick={() => fetchGrants()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State — no active grants and no error */}
      {!loading && !loadError && grants.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200 mb-6">
          <p className="text-gray-500 text-lg">No active grants</p>
          <p className="text-gray-400 text-sm mt-2">
            Check back later for new opportunities
          </p>
        </div>
      )}

      {/* Main Card */}
      <div
        className={`rounded-[14px] shadow-sm ${
          view === "cal"
            ? "bg-white border border-[#E5E7EB] p-4 md:p-8"
            : "p-2"
        }`}
      >
        {view === "cal" ? (
          <CalendarView
            month={month}
            onPrev={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
            onNext={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
            onMonthChange={setMonth}
            grants={grants}
            loading={loading}
          />
        ) : (
          <ListView
            grants={grants}
            startingId={startingId}
            onStart={handleStart}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}

/* ── Calendar View ─────────────────────────────────────── */

function CalendarView({
  month,
  onPrev,
  onNext,
  onMonthChange,
  grants,
  loading,
}: {
  month: Date;
  onPrev: () => void;
  onNext: () => void;
  onMonthChange: (d: Date) => void;
  grants: Grant[];
  loading: boolean;
}) {
  const grid = buildMonthGrid(month);
  const curMonth = month.getMonth();

  const grantsByDay = React.useMemo(() => {
    const map: Record<string, Grant[]> = {};
    grants.forEach((g) => {
      if (!map[g.date]) map[g.date] = [];
      map[g.date].push(g);
    });
    return map;
  }, [grants]);

  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <>
      {/* Month navigation — now with dropdowns */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center gap-3">
          {/* Prev arrow */}
          <button
            onClick={onPrev}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-black/[0.05] transition-colors"
            title="Previous month"
          >
            <span className="text-2xl leading-none">‹</span>
          </button>

          {/* ✅ Month + Year dropdowns */}
          <MonthYearSelector month={month} onChange={onMonthChange} />

          {/* Next arrow */}
          <button
            onClick={onNext}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-black/[0.05] transition-colors"
            title="Next month"
          >
            <span className="text-2xl leading-none">›</span>
          </button>

          {/* Today button */}
          <button
            onClick={() => onMonthChange(new Date())}
            className="h-8 px-3 rounded-lg border border-[#D6DADF] bg-white text-xs font-semibold text-[#374151] hover:bg-gray-50 transition-colors ml-1"
          >
            Today
          </button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 text-center text-[12px] rounded-t-md overflow-hidden font-semibold">
        {dow.map((d) => (
          <div key={d} className="py-2 bg-primary text-white">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar cells */}
      <div className="grid grid-cols-7 gap-[1px] bg-[#D6DADF] rounded-b-md border overflow-hidden">
        {grid.map((d, i) => {
          const inMonth = d.getMonth() === curMonth;
          const key = toKey(d);
          const dayGrants = grantsByDay[key] || [];
          const isFirstRow = i < 7;
          const isLastRow = i >= 28;
          const diagonal =
            (!inMonth && (isFirstRow || isLastRow)) ||
            (!inMonth && (i % 7 === 0 || i % 7 === 6));

          // Highlight today
          const todayKey = toKey(new Date());
          const isToday = key === todayKey;

          return (
            <div key={key} className="bg-white h-[120px] relative overflow-hidden">
              {/* Hatching for out-of-month days */}
              {!inMonth && (
                <div
                  className={`absolute inset-0 ${
                    diagonal
                      ? "bg-[repeating-linear-gradient(135deg,#eef1f4_0,#eef1f4_10px,#ffffff_10px,#ffffff_20px)]"
                      : "bg-[#f8fafc]"
                  }`}
                />
              )}

              <div className="relative h-full p-2 flex flex-col">
                {/* Date number */}
                <div className="flex items-center gap-1">
                  <div
                    className={`text-sm leading-none w-6 h-6 flex items-center justify-center rounded-full
                      ${isToday
                        ? "bg-primary text-white font-bold"
                        : inMonth
                        ? "text-[#616161] font-semibold"
                        : "text-[#BBBBBB]"
                      }`}
                  >
                    {d.getDate()}
                  </div>
                </div>

                {/* Grant pills */}
                <div className="mt-1 flex-1 overflow-y-auto pr-[2px] space-y-[3px]">
                  {loading
                    ? Array.from({ length: 2 }).map((_, idx) => (
                        <div
                          key={idx}
                          className="h-[18px] rounded bg-gray-200 animate-pulse"
                        />
                      ))
                    : dayGrants.map((g) => (
                        <div
                          key={g.id}
                          className="relative flex items-center"
                          style={{ marginLeft: 0 }}
                        >
                          <span
                            className={`absolute left-0 top-0 bottom-0 w-[5px] rounded-l-[4px] ${pillAccent[g.tone]}`}
                            style={{ minHeight: 22 }}
                          />
                          <div
                            className={`
                              w-full truncate
                              pl-[10px] pr-[5px] py-[3px] ml-[5px]
                              rounded-[5px] text-[9px] font-semibold
                              shadow-sm
                              ${pillBg[g.tone]}
                              ${pillText[g.tone]}
                            `}
                            title={g.title}
                          >
                            {g.title}
                          </div>
                        </div>
                      ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <FooterLegend />
      </div>
    </>
  );
}

/* ── List View ─────────────────────────────────────────── */

function ListView({
  grants,
  startingId,
  onStart,
  loading,
}: {
  grants: Grant[];
  startingId: string | null;
  onStart: (g: Grant) => void;
  loading: boolean;
}) {
  const { upcoming, past } = React.useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const upcomingGrants: Grant[] = [];
    const pastGrants: Grant[] = [];

    grants.forEach((g) => {
      const grantDate = parseDateOnly(g.date);
      grantDate.setHours(0, 0, 0, 0);
      if (grantDate < now) {
        pastGrants.push(g);
      } else {
        upcomingGrants.push(g);
      }
    });

    return {
      upcoming: upcomingGrants.sort((a, b) => a.date.localeCompare(b.date)),
      past: pastGrants.sort((a, b) => b.date.localeCompare(a.date)),
    };
  }, [grants]);

  const upcomingGroups = React.useMemo(() => {
    const map: Record<string, Grant[]> = {};
    upcoming.forEach((g) => {
      if (!map[g.date]) map[g.date] = [];
      map[g.date].push(g);
    });
    return Object.entries(map).map(([date, items]) => {
      const lbl = parseDateOnly(date).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      return { date, label: lbl, items };
    });
  }, [upcoming]);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-20 rounded-lg bg-gray-200 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming */}
      {upcomingGroups.map((grp) => (
        <div key={grp.date} className="space-y-4">
          <div className="text-2xl font-medium">{grp.label}</div>
          {grp.items.map((g) => {
            const startingThis = startingId === g.id;
            return (
              <div
                key={g.id}
                className="rounded-[10px] border border-[#E5E7EB] bg-white shadow-sm px-4 py-3 flex items-start justify-between"
              >
                <div className="flex gap-3 min-w-0">
                  <div
                    className={`w-[4px] min-h-[48px] mt-1 rounded-full flex-shrink-0 ${pillAccent[g.tone]}`}
                  />
                  <div className="min-w-0">
                    <div className="text-xl font-medium">{g.title}</div>
                    <div className="text-sm text-[#777B7F] font-medium line-clamp-2">
                      {g.blurb}
                    </div>
                  </div>
                </div>
                <button
                  className="text-sm px-3 py-[6px] text-[#777B7F] font-bold hover:bg-black/[0.04] hover:rounded-lg whitespace-nowrap ml-4 flex-shrink-0"
                  disabled={startingThis}
                  onClick={() => onStart(g)}
                >
                  {startingThis ? "Opening..." : "Start application"}
                </button>
              </div>
            );
          })}
        </div>
      ))}

      {/* Past */}
      {past.length > 0 && (
        <div className="pt-2 space-y-2">
          <div className="text-2xl font-medium">Past</div>
          {past.map((g) => {
            const startingThis = startingId === g.id;
            return (
              <div
                key={g.id}
                className="rounded-[10px] border border-[#E5E7EB] bg-white shadow-sm px-4 py-3 flex items-start justify-between opacity-70"
              >
                <div className="flex gap-3 min-w-0">
                  <div
                    className={`w-[4px] min-h-[48px] mt-1 rounded-full flex-shrink-0 ${tinyDot.gray}`}
                  />
                  <div className="min-w-0">
                    <div className="text-xl font-medium text-[#0f172a]">
                      {g.title}
                    </div>
                    <div className="text-sm text-[#777B7F] font-medium line-clamp-2">
                      {g.blurb}
                    </div>
                  </div>
                </div>
                <button
                  className="text-sm px-3 py-[6px] text-[#777B7F] font-bold hover:bg-black/[0.04] hover:rounded-lg whitespace-nowrap ml-4 flex-shrink-0"
                  disabled={startingThis}
                  onClick={() => onStart(g)}
                >
                  {startingThis ? "Opening..." : "View details"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8">
        <FooterLegend />
      </div>
    </div>
  );
}